import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FaBook, FaPlus, FaTimes, FaEdit, FaTrash, FaSearch, FaExternalLinkAlt, FaEye, FaPrint, FaArrowLeft, FaExclamationCircle } from 'react-icons/fa';
import researchApi from '../../../services/researchApi';
import { ChartCard, EmptyState, baseBarOptions } from '../analytics/analyticsShared';

const CATEGORY_LABELS = {
  market_research: 'Market Research',
  competitor_analysis: 'Competitor Analysis',
  product_development: 'Product Development',
  architecture: 'Architecture',
  testing: 'Testing',
  sprint_report: 'Sprint Report',
  release_notes: 'Release Notes',
  other: 'Other',
};

const EMPTY_FORM = { title: '', category: 'market_research', content: '', externalLink: '', tags: '' };

// Splits document content into paragraphs, rendering "--- LABEL ---" style
// markers as highlighted callout boxes instead of plain text, so long
// research documents read cleanly (and screenshot well) instead of
// appearing as one dense block.
function DocumentBody({ content }) {
  const blocks = content.split(/\n\s*\n/);
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        const calloutMatch = block.trim().match(/^---\s*(.+?)\s*---\s*\n?([\s\S]*)$/);
        if (calloutMatch) {
          const [, label, body] = calloutMatch;
          return (
            <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <FaExclamationCircle className="text-amber-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">{label}</div>
                <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{body}</p>
              </div>
            </div>
          );
        }
        // Short, title-cased lines with no trailing period read as headings.
        const isHeading = block.length < 70 && !block.includes('\n') && !/[.:]$/.test(block.trim());
        if (isHeading) {
          return <h3 key={i} className="text-base font-semibold text-gray-800 pt-1">{block.trim()}</h3>;
        }
        return <p key={i} className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">{block}</p>;
      })}
    </div>
  );
}

// A real archive for research and competitor-analysis evidence (Q12).
// Entries are added when the underlying work actually happens — this page
// stores documentation, it doesn't generate research on its own.
function ResearchArchive() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [viewingDoc, setViewingDoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search.trim()) params.q = search.trim();
      const res = await researchApi.list(params);
      setDocs(res.documents || []);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  const categoryChartData = useMemo(() => {
    const counts = {};
    Object.keys(CATEGORY_LABELS).forEach((k) => { counts[k] = 0; });
    docs.forEach((d) => { if (counts[d.category] != null) counts[d.category]++; });
    const entries = Object.entries(counts).filter(([, v]) => v > 0);
    return {
      labels: entries.map(([k]) => CATEGORY_LABELS[k]),
      datasets: [{ data: entries.map(([, v]) => v), backgroundColor: '#1e5c33', borderRadius: 4 }],
    };
  }, [docs]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };

  const openEdit = (doc) => {
    setForm({ ...doc, tags: (doc.tags || []).join(', ') });
    setEditing(doc._id);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing === 'new') {
        await researchApi.create(payload);
      } else {
        await researchApi.update(editing, payload);
      }
      closeForm();
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this document from the archive?')) return;
    try {
      await researchApi.remove(id);
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading archive…</div>;
  }

  if (viewingDoc) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto print:p-0 print:max-w-none">
        <style>{`
          @media print {
            nav, aside, .no-print { display: none !important; }
            body { background: white; }
          }
        `}</style>

        <div className="no-print flex items-center justify-between mb-6">
          <button
            onClick={() => setViewingDoc(null)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
          >
            <FaArrowLeft /> Back to archive
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => openEdit(viewingDoc)}
              className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <FaEdit /> Edit
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
            >
              <FaPrint /> Print / Save as PDF
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 print:shadow-none print:border-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
              {CATEGORY_LABELS[viewingDoc.category]}
            </span>
            <span className="text-xs text-gray-400">
              Added {new Date(viewingDoc.createdAt).toLocaleDateString()}
              {viewingDoc.createdBy && <> by {viewingDoc.createdBy.firstName} {viewingDoc.createdBy.lastName}</>}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-6 leading-snug">{viewingDoc.title}</h1>

          <DocumentBody content={viewingDoc.content} />

          {viewingDoc.externalLink && (
            <a
              href={viewingDoc.externalLink}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <FaExternalLinkAlt /> View linked document
            </a>
          )}

          {viewingDoc.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-6 pt-4 border-t border-gray-100">
              {viewingDoc.tags.map((t) => (
                <span key={t} className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Research &amp; Competitor Analysis Archive</h1>
          <p className="text-xs text-gray-400 mt-1">
            Market research, competitor analysis, product development, and testing records — added as the work happens.
          </p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90">
          <FaPlus /> Add document
        </button>
      </div>

      {docs.length > 0 && (
        <ChartCard title="Documents by category" className="h-64">
          <div className="h-48">
            <Bar data={categoryChartData} options={baseBarOptions} />
          </div>
        </ChartCard>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`text-xs px-3 py-1.5 rounded-full border ${categoryFilter === 'all' ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          All
        </button>
        {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setCategoryFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${categoryFilter === k ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            {label}
          </button>
        ))}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1 ml-auto">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title/content/tags…"
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-56"
          />
          <button type="submit" className="text-gray-500 px-2"><FaSearch /></button>
        </form>
      </div>

      {editing && (
        <ChartCard title={editing === 'new' ? 'Add document' : 'Edit document'}>
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2">
                {Object.entries(CATEGORY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">External link (optional)</label>
              <input value={form.externalLink} onChange={(e) => setForm({ ...form, externalLink: e.target.value })} placeholder="https://…" className="w-full border border-gray-200 rounded-lg px-3 py-2" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Content</label>
              <textarea required rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2" />
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={closeForm} className="text-sm px-3 py-2 rounded-lg border border-gray-200 flex items-center gap-1"><FaTimes /> Cancel</button>
              <button type="submit" className="text-sm px-3 py-2 rounded-lg bg-primary text-white">Save</button>
            </div>
          </form>
        </ChartCard>
      )}

      {docs.length === 0 ? (
        <EmptyState icon={<FaBook />} title="No documents yet" message="Add a document whenever real research, competitor analysis, or a development report is produced." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {docs.map((doc) => (
            <ChartCard key={doc._id} title={doc.title}>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                    {CATEGORY_LABELS[doc.category]}
                  </span>
                  <span className="text-xs text-gray-400">{new Date(doc.createdAt).toLocaleDateString()}</span>
                </div>
                <p className="text-gray-600 whitespace-pre-wrap line-clamp-3">{doc.content}</p>
                <button
                  onClick={() => setViewingDoc(doc)}
                  className="text-xs text-primary font-medium flex items-center gap-1 hover:underline"
                >
                  <FaEye /> View full document
                </button>
                {doc.externalLink && (
                  <a href={doc.externalLink} target="_blank" rel="noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                    <FaExternalLinkAlt /> View linked document
                  </a>
                )}
                {doc.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {doc.tags.map((t) => <span key={t} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{t}</span>)}
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <button onClick={() => openEdit(doc)} className="text-xs text-primary flex items-center gap-1 hover:opacity-80"><FaEdit /> Edit</button>
                  <button onClick={() => handleDelete(doc._id)} className="text-xs text-red-500 flex items-center gap-1 hover:opacity-80"><FaTrash /> Remove</button>
                </div>
              </div>
            </ChartCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default ResearchArchive;
